import { TrackingService } from '../services/TrackingService'
import { FormContinued } from '../events/FormContinued'
import type { FormContext, FormField, StepContext } from '../types'

export interface UseFormContinuedProps {
  formContext: FormContext
  stepContext: StepContext
  field: FormField[]
}

/**
 * Hook for tracking form continuation events
 *
 * Tracks when a user continues to the next step in a multi-step form.
 * This is useful for understanding form completion rates and identifying
 * where users drop off in multi-step flows.
 *
 * @returns A function that tracks form continuation events
 *
 * @example
 * ```typescript
 * const trackFormContinued = useFormContinued()
 *
 * const handleNextStep = () => {
 *   trackFormContinued({
 *     field: [
 *       { fieldName: 'email', fieldValue: 'user@example.com' },
 *       { fieldName: 'name', fieldValue: 'John Doe' }
 *     ],
 *     formContext: {
 *       formName: 'signup',
 *       formId: 'form_123',
 *       formType: 'multi-step'
 *     },
 *     stepContext: {
 *       stepId: '1',
 *       stepName: 'Step 1',
 *       stepNumber: 1,
 *     },
 *   })
 * }
 * ```
 */
const useFormContinued =
  () =>
  (props: UseFormContinuedProps): void => {
    const { formContext, stepContext, field } = props

    if (!formContext || !stepContext || !field) {
      console.error('Missing required parameters for form continuation.')
      return
    }

    const formContinuedEventPayload = new FormContinued({
      formContext,
      stepContext,
      field,
    })

    TrackingService.track(formContinuedEventPayload)
  }

export default useFormContinued
